import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MailCheck, MailWarning, Loader2, LogOut, WifiOff } from "lucide-react";

const inflight = new Map();
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function verifyWithRetry(token) {
  for (let tries = 0; ; tries++) {
    try {
      const { data } = await api.get("/auth/verify", { params: { token }, timeout: 20000 });
      return { ok: true, data };
    } catch (e) {
      if (e?.response) return { ok: false, error: apiError(e) };
      if (tries >= 3) return { ok: false, offline: true };
      await sleep(2500 * (tries + 1));
    }
  }
}

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [state, setState] = useState(token ? "verifying" : "pending"); // verifying | success | error | offline | pending
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [attempt, setAttempt] = useState(0);
  let continueTarget = "/auth";
  if (user) continueTarget = user.onboarded ? "/match" : "/onboarding";

  useEffect(() => {
    if (!token) return;
    const key = `${token}#${attempt}`;
    if (!inflight.has(key)) inflight.set(key, verifyWithRetry(token));
    setState("verifying");
    inflight.get(key).then((res) => {
      if (res.ok) {
        setState("success");
        if (res.data.user) { setUser(res.data.user); nav(res.data.user.onboarded ? "/match" : "/onboarding", { replace: true }); }
      } else if (res.offline) {
        setState("offline");
      } else {
        setError(res.error); setState("error");
      }
    });
  }, [token, attempt, setUser, nav]);

  useEffect(() => { if (cooldown <= 0) return; const t = setTimeout(() => setCooldown((c) => c - 1), 1000); return () => clearTimeout(t); }, [cooldown]);

  const resend = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent — check your inbox"); setCooldown(60); }
    catch (e) { toast.error(apiError(e)); if (e?.response?.status === 429) setCooldown(60); }
  };

  return (
    <div className="min-h-screen orbit-bg-grid flex items-center justify-center p-4" data-testid="verify-email-page">
      <div className="orbit-glass rounded-3xl p-8 max-w-md w-full text-center orbit-fade-up">
        <Link to="/" className="font-display text-xl font-extrabold tracking-tight">ORBIT<span className="orbit-gradient-text">HELLO</span></Link>
        {state === "verifying" && (<><Loader2 className="w-10 h-10 mx-auto mt-6 text-cyan-300 animate-spin" /><h1 className="font-display text-2xl font-bold mt-4">Verifying your email…</h1></>)}
        {state === "success" && (
          <>
            <MailCheck className="w-12 h-12 mx-auto mt-6 text-emerald-300" />
            <h1 className="font-display text-2xl font-bold mt-4" data-testid="verify-success">Email verified</h1>
            <p className="text-slate-400 mt-2 text-sm">Your account is confirmed. Taking you into Orbit Hello…</p>
            <button onClick={() => nav(continueTarget)} className="orbit-btn-primary w-full mt-6" data-testid="verify-continue-btn">{user ? "Continue" : "Log in"}</button>
          </>
        )}
        {state === "error" && (
          <>
            <MailWarning className="w-12 h-12 mx-auto mt-6 text-rose-300" />
            <h1 className="font-display text-2xl font-bold mt-4" data-testid="verify-error">Link not valid</h1>
            <p className="text-slate-400 mt-2 text-sm" data-testid="verify-error-detail">{error}</p>
            {user && !user.email_verified && <button onClick={resend} disabled={cooldown > 0} className="orbit-btn-primary w-full mt-6 disabled:opacity-60" data-testid="verify-resend-btn">{cooldown > 0 ? `Resend available in ${cooldown}s` : "Send a new link"}</button>}
            {!user && <button onClick={() => nav("/auth")} className="orbit-btn-primary w-full mt-6">Log in to request a new link</button>}
          </>
        )}
        {state === "offline" && (
          <>
            <WifiOff className="w-12 h-12 mx-auto mt-6 text-amber-300" />
            <h1 className="font-display text-2xl font-bold mt-4" data-testid="verify-offline">Couldn't reach Orbit Hello</h1>
            <p className="text-slate-400 mt-2 text-sm">Your link looks fine, but we couldn't connect to the server. Check your internet connection and try again — the link stays valid.</p>
            <button onClick={() => setAttempt((a) => a + 1)} className="orbit-btn-primary w-full mt-6" data-testid="verify-retry-btn">Try again</button>
          </>
        )}
        {state === "pending" && (
          <>
            <div className="w-14 h-14 mx-auto mt-6 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center"><MailCheck className="w-6 h-6 text-cyan-300" /></div>
            <h1 className="font-display text-2xl font-bold mt-4" data-testid="verify-pending">Check your inbox</h1>
            <p className="text-slate-400 mt-2 text-sm">We sent a verification link to <span className="text-slate-100 font-semibold" data-testid="verify-email-address">{user?.email}</span>. Tap it to unlock live video matching. The link expires in 24 hours.</p>
            <button onClick={resend} disabled={cooldown > 0} className="orbit-btn-primary w-full mt-6 disabled:opacity-60" data-testid="verify-resend-btn">{cooldown > 0 ? `Resend available in ${cooldown}s` : "Resend email"}</button>
            <button onClick={async () => { await logout(); nav("/auth"); }} className="mt-4 text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="verify-logout-btn"><LogOut className="w-3 h-3" /> Use a different account</button>
          </>
        )}
      </div>
    </div>
  );
}
